import React, { useState, useCallback, useEffect } from 'react';

const VariantPicker = ({ 
  layerKey, 
  variants, 
  valueMap, 
  onChange, 
  onlyShowCompatible, 
  onToggleOnlyCompat 
}) => {
  const [hoveredVariant, setHoveredVariant] = useState(null);



  // Notify parent of hovered variant for live preview
  useEffect(() => {
    if (hoveredVariant) {
      const variant = variants.find(v => v.id === hoveredVariant);
      if (variant) {
        // This would be passed via a callback prop in a real implementation
        // For now, we'll use a custom event
        window.dispatchEvent(new CustomEvent('variantHover', {
          detail: { ...variant, layerKey }
        }));
      }
    }
  }, [hoveredVariant, variants, layerKey]);

  const handleVariantClick = useCallback((variantId, event) => {
    const currentValue = valueMap[variantId] || null;
    let nextValue;

    if (event.shiftKey || event.button === 2) {
      // Shift+Click or Right-click = only
      nextValue = currentValue === 'only' ? null : 'only';
      
      // Clear other 'only' values in this layer
      const newValueMap = { ...valueMap };
      Object.keys(newValueMap).forEach(key => {
        if (newValueMap[key] === 'only') {
          newValueMap[key] = null;
        }
      });
      newValueMap[variantId] = nextValue;
      onChange(newValueMap);
    } else {
      // Regular click: null → include → exclude → null
      if (currentValue === null) nextValue = 'include';
      else if (currentValue === 'include') nextValue = 'exclude';
      else nextValue = null;
      
      onChange({ ...valueMap, [variantId]: nextValue });
    }
  }, [valueMap, onChange]);

  const handleContextMenu = useCallback((e, variantId) => {
    e.preventDefault();
    handleVariantClick(variantId, { button: 2 });
  }, [handleVariantClick]);

  const getVariantClass = (variantId) => {
    const value = valueMap[variantId] || null;
    const baseClass = 'variant-card';
    
    if (value === 'include') return `${baseClass} variant-card--include`;
    if (value === 'exclude') return `${baseClass} variant-card--exclude`;
    if (value === 'only') return `${baseClass} variant-card--only`;
    return baseClass;
  };

  const getVariantOverlay = (variantId) => {
    const value = valueMap[variantId] || null;
    
    if (value === 'include') return <div className="variant-overlay variant-overlay--include">✓</div>;
    if (value === 'exclude') return <div className="variant-overlay variant-overlay--exclude">✗</div>;
    if (value === 'only') return <div className="variant-overlay variant-overlay--only">★</div>;
    return null;
  };

  return (
    <div className="variant-picker">
      <div className="variant-picker-header">
        <h3 className="variant-picker-title">
          {layerKey.charAt(0).toUpperCase() + layerKey.slice(1)} Variants
        </h3>
        <div className="variant-picker-controls">
          <button
            className={`compatibility-toggle ${onlyShowCompatible ? 'active' : ''}`}
            onClick={onToggleOnlyCompat}
          >
            {onlyShowCompatible ? '🔗' : '🔓'} Compatible Only
          </button>
        </div>
      </div>
      

      
      <div className="variant-grid">
        {variants.map(variant => (
          <div
            key={variant.id}
            className={getVariantClass(variant.id)}
            onClick={(e) => handleVariantClick(variant.id, e)}
            onContextMenu={(e) => handleContextMenu(e, variant.id)}
            onMouseEnter={() => setHoveredVariant(variant.id)}
            onMouseLeave={() => setHoveredVariant(null)}
          >
            <img
              src={variant.src}
              alt={`${layerKey} variant ${variant.id}`}
              className="variant-thumbnail"
            />
            {getVariantOverlay(variant.id)}
            <div className="variant-id">{variant.id}</div>
          </div>
        ))}
      </div>
      
      <div className="variant-picker-help">
        <p>Click: include → exclude → off | Shift+Click/Right-click: only</p>
      </div>
    </div>
  );
};

export default VariantPicker;
